import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALGROUPCONTRACT_LOAD_BY_TRIAL_GROUP_ID,
    ACTION_TRIALGROUPCONTRACT_LOAD_BY_ID,
    ACTION_TRIALGROUPCONTRACT_LOAD_FAILURE,
    ACTION_TRIALGROUPCONTRACT_LOAD_SUCCESS,

    ACTION_TRIALGROUPCONTRACT_SAVE,
    ACTION_TRIALGROUPCONTRACT_SAVE_SUCCESS,
    ACTION_TRIALGROUPCONTRACT_SAVE_FAILURE,

    ACTION_TRIALGROUPCONTRACT_VALIDATE,
    ACTION_TRIALGROUPCONTRACT_VALIDATE_SUCCESS,
    ACTION_TRIALGROUPCONTRACT_VALIDATE_FAILURE,

    ACTION_TRIALGROUPCONTRACT_CREATE,
    ACTION_TRIALGROUPCONTRACT_CREATE_SUCCESS,
    ACTION_TRIALGROUPCONTRACT_CREATE_FAILURE,

    ACTION_TRIALGROUPCONTRACT_DELETE,
    ACTION_TRIALGROUPCONTRACT_DELETE_SUCCESS,
    ACTION_TRIALGROUPCONTRACT_DELETE_FAILURE,

    ACTION_TRIALGROUPCONTRACT_CLEAR,
    ACTION_TRIALGROUPCONTRACT_FILTER,

    TrialGroupContractAction
} from '../actions/trialGroupContract';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialGroupContractState {
    formData: Dtos.TrialGroupContract | Dtos.TrialGroupContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    deleteState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialGroupContract[];
    formLength: number;
}

const initialState: ITrialGroupContractState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    deleteState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialGroupContractState, _isLoading: boolean): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialGroupContractState,
    formData: Dtos.TrialGroupContract | Dtos.TrialGroupContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialGroupContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialGroupContractState, formFilter: IFormFilter): ITrialGroupContractState {

    const items: Dtos.TrialGroupContract[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialGroupContractState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialGroupContractState, propertiesToClear?: string[]): ITrialGroupContractState {

    if (!propertiesToClear) {
        const newState: ITrialGroupContractState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialGroupContractState, _isSaving: boolean): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialGroupContractState,
    formData: Dtos.TrialGroupContract | Dtos.TrialGroupContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialGroupContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ITrialGroupContractState, _isValidating: boolean): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrialGroupContractState,
    formData: Dtos.TrialGroupContract | Dtos.TrialGroupContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrialGroupContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialGroupContractState, _isCreating: boolean): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialGroupContractState,
    formData: Dtos.TrialGroupContract,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialGroupContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function deleting(state: ITrialGroupContractState, _isDeleting: boolean): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function deleteSuccess(
    state: ITrialGroupContractState,
): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function deleteFailure(state: ITrialGroupContractState, responseStatus: Dtos.ResponseStatus): ITrialGroupContractState {
    const newState: ITrialGroupContractState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialGroupContractReducer: Reducer<ITrialGroupContractState> = (state: ITrialGroupContractState = initialState, action: TrialGroupContractAction) => {
    switch (action.type) {
        case ACTION_TRIALGROUPCONTRACT_LOAD_BY_TRIAL_GROUP_ID:
        case ACTION_TRIALGROUPCONTRACT_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALGROUPCONTRACT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALGROUPCONTRACT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPCONTRACT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALGROUPCONTRACT_SAVE:
            return saving(state, true);
        case ACTION_TRIALGROUPCONTRACT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPCONTRACT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPCONTRACT_VALIDATE:
            return validating(state, true);
        case ACTION_TRIALGROUPCONTRACT_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPCONTRACT_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPCONTRACT_CREATE:
            return creating(state, true);
        case ACTION_TRIALGROUPCONTRACT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALGROUPCONTRACT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPCONTRACT_DELETE:
            return deleting(state, true);
        case ACTION_TRIALGROUPCONTRACT_DELETE_SUCCESS:
            return deleteSuccess(state);
        case ACTION_TRIALGROUPCONTRACT_DELETE_FAILURE:
            return deleteFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPCONTRACT_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default TrialGroupContractReducer;